import React, { useState, useEffect, useCallback } from 'react'
import {
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Button,
  CircularProgress,
  Container,
  Box,
  Typography,
} from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import ClearIcon from '@mui/icons-material/Clear'
import { useAtom, useSetAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  transcriptAtom,
  isListeningAtom,
  historySearchesAtom,
  promptAtom,
  pictogramsAtom,
  categoriesAtom,
  selectedPictogramsAtom,
} from 'atoms'

import { useSpeechRecognition } from './useSpeechRecognition'
import { generatePictogramData, boardModel } from './utils'

function SearchField() {
  const [transcript, setTranscript] = useAtom(transcriptAtom)
  const [isListening] = useAtom(isListeningAtom)
  const { startListening } = useSpeechRecognition()
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState(transcript || '')
  const suggestions = useAtomValue(historySearchesAtom)
  const [loading, setLoading] = useState(false)
  const prompt = useAtomValue(promptAtom)
  const [historySearches, setHistorySearches] = useAtom(historySearchesAtom)
  const setPictograms = useSetAtom(pictogramsAtom)
  const setCategories = useSetAtom(categoriesAtom)
  const setSelectedPictograms = useSetAtom(selectedPictogramsAtom)

  const generateAsyncStory = useCallback(
    async (newValue) => {
      if (!newValue?.trim()) return

      setLoading(true)
      setPictograms({})
      setSelectedPictograms([])

      const updatedHistory = Array.from(new Set([...historySearches, newValue]))
      setHistorySearches(updatedHistory)

      try {
        const combinedPrompt1 = `${prompt}\n${newValue}`

        // Fetch the first result
        const result1 = await boardModel.generateContent(combinedPrompt1)

        // Access the text function
        const generatedModel = await result1.response.text()

        // Parse and process
        console.log(generatedModel, 'generatedModel')
        const parsedModel = JSON.parse(
          generatedModel.replace(/```json\n|\n```/g, '')
        )
        console.log(parsedModel, 'parsedModel')
        const categories = parsedModel.map((entry) => entry.category)
        const pictograms = await generatePictogramData(parsedModel)
        console.log(pictograms)
        console.log(categories)
        setPictograms(pictograms)
        setCategories(categories)
      } catch (error) {
        console.error('Error generating content:', error)
        alert('Error generating story.')
      } finally {
        setLoading(false)
      }
    },
    [prompt, historySearches, setPictograms, setHistorySearches]
  )

  useEffect(() => {
    setInputValue(transcript)
  }, [transcript])

  const handleInputChange = (event, newValue) => {
    setInputValue(newValue)
    setTranscript(newValue)
    if (event.code === 'Enter') {
      generateAsyncStory(newValue)
    }
  }

  const handleClearInput = () => {
    setInputValue('')
    setTranscript('')
  }

  // Handle selection of an option from suggestions
  const handleSuggestionSelect = (event, newValue) =>
    generateAsyncStory(newValue)

  // Render option with bold match text
  const renderOption = ({ key, ...props }, option) => {
    const matchIndex = option.toLowerCase().indexOf(inputValue.toLowerCase())
    if (matchIndex === -1 || inputValue.length < 2) {
      return (
        <li key={key} {...props}>
          {option}
        </li>
      )
    }

    const beforeMatch = option.slice(0, matchIndex)
    const matchText = option.slice(matchIndex, matchIndex + inputValue.length)
    const afterMatch = option.slice(matchIndex + inputValue.length)

    return (
      <li key={key} {...props}>
        <Typography variant="body2" component="span">
          {beforeMatch}
          <Typography variant="body2" component="span" fontWeight="bold">
            {matchText}
          </Typography>
          {afterMatch}
        </Typography>
      </li>
    )
  }

  return (
    <Container maxWidth="lg">
      <Autocomplete
        freeSolo
        options={suggestions}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleSuggestionSelect} // Added this to handle suggestion selection
        renderOption={renderOption} // Use custom renderOption
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={t('searchHint')}
            // onKeyDown={handleKeyDown} // Keep this to listen for Enter key
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearInput}
                    edge="end"
                    style={{
                      visibility: inputValue ? 'visible' : 'hidden',
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                  <IconButton
                    onClick={startListening}
                    disabled={isListening}
                    edge="end"
                    // style={{ paddingRight: inputValue ? '0px' : '30px' }}
                  >
                    <MicIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Box
        mt={2}
        sx={{
          display: 'flex', // Makes the Box a flex container
          justifyContent: 'center', // Centers the button horizontally
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => generateAsyncStory(inputValue)}
          disabled={loading || !inputValue.trim()}
          sx={{
            position: 'relative',
            // Prevent the button from changing size when loading
            minWidth: '120px', // Adjust this value based on your button's content
            '& .MuiCircularProgress-root': {
              position: 'absolute',
              left: '50%',
              top: '50%',
              marginLeft: '-12px', // Half of CircularProgress size
              marginTop: '-12px', // Half of CircularProgress size
            },
          }}
        >
          {t('generateBoard')}
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                color: 'primary.light',
              }}
            />
          )}
        </Button>
      </Box>
    </Container>
  )
}

export default SearchField
